"use client";
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  AlertCircle,
  Shield,
} from "lucide-react";
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../../Component/ui/card";
import { useNavigate } from "react-router-dom";
import DashboardFilters from "../../Component/dashboard-filters";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../Component/ui/chart";
import API_URL from "../../../src/Config";
import "./allSection.css";

const StockPRSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department_id: "",
    date_preset: "all",
    showCustomDate: false,
    start_date: "",
    end_date: "",
  });

  const [summary, setSummary] = useState({
    purchase_requests: {},
    purchase_orders: {},
  });

  const [charts, setCharts] = useState({
    pr_status_funnel: {},
    overdue_by_department: [],
    pr_age_analysis: [],
    po_status_distribution: {},
    monthly_po_trend: [],
    top_vendors: [],
  });

  const fetchPRData = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = new URLSearchParams();
      if (
        currentFilters.date_preset &&
        currentFilters.date_preset !== "custom"
      ) {
        params.append("filter_option", currentFilters.date_preset);
      }
      if (currentFilters.department_id) {
        params.append("department_id", currentFilters.department_id);
      }
      if (currentFilters.start_date)
        params.append("start_date", currentFilters.start_date);
      if (currentFilters.end_date)
        params.append("end_date", currentFilters.end_date);

      const response = await axios.get(
        `${API_URL}/api/kpi/purchase-requests?${params.toString()}`,
        {
          headers: { Authorization: authKey },
        },
      );

      if (response.status === 200) {
        setSummary(response.data.summary);
        setCharts(response.data.charts);
      }
    } catch (error) {
      console.error("Error fetching PR KPIs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchPRData(newFilters);
  };

  const handleResetFilters = () => {
    const reset = {
      department_id: "",
      date_preset: "all",
      showCustomDate: false,
      start_date: "",
      end_date: "",
    };
    setFilters(reset);
    fetchPRData(reset);
  };

  useEffect(() => {
    fetchPRData();
  }, []);

  // Card click handlers
  const handlePRCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);

    const filterOptionValue = filters.date_preset ? filters.date_preset : "All";
    params.append("filter_option", filterOptionValue);

    if (filters.department_id && filters.department_id.trim() !== "") {
      params.append("department_id", filters.department_id);
    }

    if (
      filters.start_date &&
      filters.end_date &&
      filters.start_date.trim() !== "" &&
      filters.end_date.trim() !== ""
    ) {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }

    console.log("Navigating to PR List with params:", params.toString());

    window.open(`/pr-list?${params.toString()}`, "_blank");
    // navigate(`/pr-list?${params.toString()}`);
  };

  const handlePOCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);

    const filterOptionValue = filters.date_preset ? filters.date_preset : "All";
    params.append("filter_option", filterOptionValue);

    if (filters.department_id && filters.department_id.trim() !== "") {
      params.append("department_id", filters.department_id);
    }

    if (
      filters.start_date &&
      filters.end_date &&
      filters.start_date.trim() !== "" &&
      filters.end_date.trim() !== ""
    ) {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }

    console.log("Navigating to PO List with params:", params.toString());

    window.open(`/po-list-stock-pr?${params.toString()}`, "_blank");
    // navigate(`/po-list-stock-pr?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <div>Loading...</div>
      </div>
    );
  }

  const { purchase_requests, purchase_orders } = summary;
  const {
    pr_status_funnel,
    overdue_by_department,
    pr_age_analysis,
    po_status_distribution,
    monthly_po_trend,
  } = charts;

  // Helper to check if dataset has any valid values
  const hasValidData = (data, keys = []) => {
    if (!data) return false;
    if (Array.isArray(data))
      return data.some((d) => Object.values(d).some((v) => v > 0));
    return keys.some((k) => data[k] && data[k] > 0);
  };

  return (
    <div className="pr-section">
      {/* Header */}
      <div className="pr-section-header">
        <div>
          <h2 className="newdashboard-section-title">
            Purchase Requests (PR) and Orders (PO)
          </h2>
          <p className="pr-subtitle">
            Monitor purchase request and order statistics
          </p>
        </div>
        <DashboardFilters
          filters={filters}
          onFilterChange={setFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
      </div>

      {/* ---------- PR KPI CARDS - Now Clickable ---------- */}
      <div className="pr-kpi-grid">
        <Card
          className="newdashboard-card-clickable"
          onClick={() => handlePRCardClick("all")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">Total PRs Raised</CardTitle>
            <FileText className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-chart-1">
              {purchase_requests.total_prs_raised?.count || 0}
            </div>
            <p className="pr-kpi-note">
              Client:{" "}
              {purchase_requests.total_prs_raised?.breakdown?.customer || 0} |
              Own:{" "}
              {purchase_requests.total_prs_raised?.breakdown?.contractor || 0}
            </p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handlePRCardClick("approved")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">Approved PRs</CardTitle>
            <CheckCircle2 className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-chart-2">
              {purchase_requests.approved_prs?.count || 0}
            </div>
            <p className="pr-kpi-note">
              Client: {purchase_requests.approved_prs?.breakdown?.customer || 0}{" "}
              | Own:{" "}
              {purchase_requests.approved_prs?.breakdown?.contractor || 0}
            </p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handlePRCardClick("pending")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">Pending PRs</CardTitle>
            <Clock className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-chart-3">
              {purchase_requests.pending_prs?.count || 0}
            </div>
            <p className="pr-kpi-note">
              Client: {purchase_requests.pending_prs?.breakdown?.customer || 0}{" "}
              | Own: {purchase_requests.pending_prs?.breakdown?.contractor || 0}
            </p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handlePRCardClick("overdue")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">Overdue PRs</CardTitle>
            <AlertTriangle className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-destructive">
              {purchase_requests.overdue_prs?.count || 0}
            </div>
            <p className="pr-kpi-note">
              Client: {purchase_requests.overdue_prs?.breakdown?.customer || 0}{" "}
              | Own: {purchase_requests.overdue_prs?.breakdown?.contractor || 0}
            </p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handlePRCardClick("critical_overdue")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">Pending &gt;30 Days</CardTitle>
            <AlertCircle className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-destructive">
              {purchase_requests.critical_overdue_prs?.count || 0}
            </div>
            <p className="pr-kpi-note">Critical attention needed</p>
          </CardContent>
        </Card>
      </div>

      {/* ---------- PR CHARTS ---------- */}
      <div className="pr-charts-grid">
        {/* PR Status Funnel */}
        <Card>
          <CardHeader>
            <CardTitle>PR Status Funnel</CardTitle>
            <CardDescription>
              Total → Approved → Pending → Overdue
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasValidData(pr_status_funnel, [
              "total_prs",
              "approved",
              "pending",
              "overdue",
            ]) ? (
              <ChartContainer
                config={{
                  client: { label: "Client PRs", color: "hsl(217, 91%, 60%)" },
                  own: {
                    label: "Own Consumption",
                    color: "hsl(173, 80%, 40%)",
                  },
                }}
                className="pr-chart-box"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        stage: "Total PRs",
                        client: pr_status_funnel?.total_prs,
                        own: pr_status_funnel?.contractor_prs,
                      },
                      {
                        stage: "Approved",
                        client: pr_status_funnel?.approved,
                        own: 0,
                      },
                      {
                        stage: "Pending",
                        client: pr_status_funnel?.pending,
                        own: 0,
                      },
                      {
                        stage: "Overdue",
                        client: pr_status_funnel?.overdue,
                        own: 0,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar
                      dataKey="client"
                      fill="var(--color-client)"
                      name="Client PRs"
                    />
                    <Bar
                      dataKey="own"
                      fill="var(--color-own)"
                      name="Own Consumption"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="newdashboard-no-data">No Data Found</div>
            )}
          </CardContent>
        </Card>

        {/* Overdue PRs by Department */}
        <Card>
          <CardHeader>
            <CardTitle>Overdue PRs by Department</CardTitle>
            <CardDescription>Department-wise overdue count</CardDescription>
          </CardHeader>
          <CardContent>
            {hasValidData(overdue_by_department) ? (
              <ChartContainer
                config={{
                  overdue: { label: "Overdue", color: "hsl(0, 84%, 60%)" },
                }}
                className="pr-chart-box"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={overdue_by_department.map((d) => ({
                      department: d.department,
                      overdue: d.count,
                    }))}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="department" type="category" width={120} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="overdue"
                      fill="var(--color-overdue)"
                      name="Overdue PRs"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="newdashboard-no-data">No Data Found</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PR Age Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>PR Age Analysis</CardTitle>
          <CardDescription>Distribution of pending PRs by age</CardDescription>
        </CardHeader>
        <CardContent>
          {hasValidData(pr_age_analysis) ? (
            <ChartContainer
              config={{
                count: { label: "Count", color: "hsl(25, 95%, 53%)" },
              }}
              className="pr-chart-box"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={pr_age_analysis.map((d) => ({
                    category: d.age_range,
                    count: d.count,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend />
                  <Bar
                    dataKey="count"
                    fill="var(--color-count)"
                    name="Pending PRs"
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          ) : (
            <div className="newdashboard-no-data">No Data Found</div>
          )}
        </CardContent>
      </Card>

      {/* ---------- PO KPI CARDS - Now Clickable ---------- */}
      <h3 className="pr-title" style={{ marginTop: "2rem" }}>
        Purchase Orders (PO)
      </h3>
      <div className="pr-kpi-grid">
        <Card
          className="newdashboard-card-clickable"
          onClick={() => handlePOCardClick("all")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">Total POs Generated</CardTitle>
            <FileText className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-chart-1">
              {purchase_orders.total_pos_generated?.count || 0}
            </div>
            <p className="pr-kpi-note">
              {purchase_orders.total_pos_generated?.label}
            </p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handlePOCardClick("open")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">Open POs</CardTitle>
            <Clock className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-chart-3">
              {purchase_orders.open_pos?.count || 0}
            </div>
            <p className="pr-kpi-note">{purchase_orders.open_pos?.label}</p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handlePOCardClick("closed")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">Closed POs</CardTitle>
            <CheckCircle2 className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-chart-2">
              {purchase_orders.closed_pos?.count || 0}
            </div>
            <p className="pr-kpi-note">{purchase_orders.closed_pos?.label}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pr-card-header">
            <CardTitle className="pr-card-title">PR to PO Conversion</CardTitle>
            <Shield className="pr-card-icon" />
          </CardHeader>
          <CardContent>
            <div className="pr-kpi-value pr-chart-4">
              {purchase_orders.pr_to_po_conversion?.rate || 0}%
            </div>
            <p className="pr-kpi-note">
              {purchase_orders.pr_to_po_conversion?.label}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ---------- PO CHARTS ---------- */}
      <div className="pr-charts-grid">
        {/* PO Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>PO Status Distribution</CardTitle>
            <CardDescription>Open vs Closed Purchase Orders</CardDescription>
          </CardHeader>
          <CardContent>
            {hasValidData(po_status_distribution, ["open", "closed"]) ? (
              <ChartContainer
                config={{
                  open: { label: "Open", color: "hsl(35, 90%, 55%)" },
                  closed: { label: "Closed", color: "hsl(160, 70%, 45%)" },
                }}
                className="pr-chart-box"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        status: "Open",
                        count: po_status_distribution.open || 0,
                      },
                      {
                        status: "Closed",
                        count: po_status_distribution.closed || 0,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="status" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar
                      dataKey="count"
                      fill="hsla(189, 90%, 55%, 1.00)"
                      name="POs"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="newdashboard-no-data">No Data Found</div>
            )}
          </CardContent>
        </Card>

        {/* Monthly PO Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly PO Trend</CardTitle>
            <CardDescription>Track PO generation over months</CardDescription>
          </CardHeader>
          <CardContent>
            {hasValidData(monthly_po_trend) ? (
              <ChartContainer
                config={{
                  count: { label: "POs", color: "hsl(220, 85%, 55%)" },
                }}
                className="pr-chart-box"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={monthly_po_trend.map((d) => ({
                      month: d.month,
                      count: d.count,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar dataKey="count" fill="var(--color-count)" name="POs" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="newdashboard-no-data">No Data Found</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StockPRSection;
